import { useEffect, useMemo } from "react";

import {
  Controller,
  type SubmitErrorHandler,
  type SubmitHandler,
  type UseFormReturn,
} from "react-hook-form";

import { Button, classNames, Form, Input, Text } from "@alpacahq/ui/old";

import type { UseCountryReturn } from "../../../../../../api/hooks/useCountry";
import {
  NOT_ALLOWED_OWNER_TAX_ID_TYPES,
  NOT_SPECIFIC_TAX_ID_TYPE,
  USA,
} from "../../../constants";
import { EntityCountryField } from "../../../EntityCountryField";
import { OwnerDocumentField } from "../../../OwnerDocumentField";
import { StateInput } from "../../../StateInput";
import { UploadID } from "../../../UploadID";
import type { UseUploadIDReturn } from "../../../useUploadID";
import type { UseUploadOwnerDocumentReturn } from "../../../useUploadOwnerDocument";
import { isAllAsterisks, onTaxIdKeyDown } from "../../../util";
import { YesNoCheckbox } from "../../../YesNoCheckbox";
import type { AuthorizedIndividual, UltimateBeneficialOwner } from "../schema";
import type { FormOwnerType } from ".";

export interface AuthUBOFormProps {
  type: FormOwnerType;
  form: UseFormReturn<UltimateBeneficialOwner | AuthorizedIndividual>;
  isSubmitDisabled: boolean;
  onSubmit: SubmitHandler<UltimateBeneficialOwner | AuthorizedIndividual>;
  onSubmitError: SubmitErrorHandler<
    UltimateBeneficialOwner | AuthorizedIndividual
  >;
  country: UseCountryReturn;
  uploadID: UseUploadIDReturn;
  addressDoc: UseUploadOwnerDocumentReturn;
  taxIdDoc: UseUploadOwnerDocumentReturn;
  onDelete?: () => void;
  isTaxIDRequired: boolean;
}

export const AuthUBOForm = ({
  type,
  form,
  isSubmitDisabled,
  onSubmit,
  onSubmitError,
  country,
  uploadID,
  addressDoc,
  taxIdDoc,
  onDelete,
  isTaxIDRequired,
}: AuthUBOFormProps) => {
  const {
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = form;

  const isAffiliatedExchangeOrFinra = watch("is_affiliated_exchange_or_finra");
  const isControlPerson = watch("is_control_person");
  const isPoliticallyExposed = watch("is_politically_exposed");
  const isImmediateFamilyExposed = watch("immediate_family_exposed");

  const countryOfTaxResidence = watch("country_of_tax_residence");
  const isUSATaxResident = countryOfTaxResidence === USA;

  const taxIDTypeOptions = useMemo(() => {
    const countryOfTaxResidenceAlpha3 =
      country.nameToAlpha3(countryOfTaxResidence) || "";

    const taxIDTypes = country.infos[countryOfTaxResidenceAlpha3]
      ?.tax_id_types || [NOT_SPECIFIC_TAX_ID_TYPE];

    return taxIDTypes.filter(
      ({ id_type }) => !NOT_ALLOWED_OWNER_TAX_ID_TYPES.includes(id_type),
    );
  }, [countryOfTaxResidence, country]);

  const taxIDType = watch("tax_id_type");
  const taxIDRegex = new RegExp(
    taxIDTypeOptions.find(
      (tax_id) =>
        tax_id.id_type === taxIDType || tax_id.id_type === "NOT_SPECIFIED",
    )?.regexp || "^.*$",
  );

  useEffect(() => {
    if (!isAffiliatedExchangeOrFinra) {
      setValue("affiliated_firm", undefined);
    }
    if (!isControlPerson) {
      setValue("controlling_firms", undefined);
    }
  }, [isAffiliatedExchangeOrFinra, isControlPerson, setValue]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: isUSATaxResident is the trigger: the state field is cleared when tax residency changes. The body writes rather than reads it.
  useEffect(() => {
    setValue("state", "");
  }, [isUSATaxResident, setValue]);

  return (
    <div>
      <Form
        formMethods={form}
        onSubmit={onSubmit}
        onSubmitError={onSubmitError}
      >
        <h2 className="font-medium text-lg">Personal Information</h2>
        <div
          className={classNames(
            type === "ubo" && "flex flex-col gap-6 lg:grid lg:grid-cols-2",
          )}
        >
          <Form.TextInput
            type="text"
            label="Title at Company"
            name="title_at_company"
            placeholder="Title"
            className="bg-white"
          />
          {type === "ubo" && (
            <Form.TextInput
              type="number"
              label="Percentage Ownership"
              name="percentage_ownership"
              rightIcon={<span>%</span>}
              className="bg-white"
            />
          )}
        </div>
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
          <Form.TextInput
            type="text"
            label="First Name"
            name="given_name"
            placeholder="First Name"
            className="bg-white"
          />
          <Form.TextInput
            type="text"
            label="Middle Name"
            name="middle_name"
            placeholder="Middle Name"
            className="bg-white"
          />
        </div>
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
          <Form.TextInput
            type="text"
            label="Last Name"
            name="family_name"
            placeholder="Last Name"
            className="bg-white"
          />
          <Form.DatePickerInput
            label="Date of Birth"
            name="date_of_birth"
            placeholder="MM-DD-YYYY"
            className="bg-white"
          />
        </div>
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
          <Form.TextInput
            type="email"
            label="Email"
            name="email"
            placeholder="Email"
            className="bg-white"
          />
          <Form.TextInput
            type="text"
            label="Phone Number"
            name="phone_number"
            placeholder="Phone number"
            className="bg-white"
          />
        </div>

        <h2 className="pt-2 font-medium text-lg">Tax Information</h2>
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
          <EntityCountryField<UltimateBeneficialOwner | AuthorizedIndividual>
            isRequired={false}
            isInvalid={Boolean(errors.country_of_citizenship)}
            errorMsg={errors.country_of_citizenship?.message}
            label="Country of Citizenship"
            name="country_of_citizenship"
            control={form.control}
            countries={country.allCountries}
            placeholder="Country of Citizenship"
          />
          <EntityCountryField<UltimateBeneficialOwner | AuthorizedIndividual>
            isRequired={false}
            isInvalid={Boolean(errors.country_of_tax_residence)}
            errorMsg={errors.country_of_tax_residence?.message}
            label="Country of Tax Residence"
            name="country_of_tax_residence"
            control={form.control}
            countries={country.allowedCountries}
            placeholder="Country of Tax Residence"
          />
        </div>

        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
          <Form.SelectInput
            label="Tax ID Type"
            name="tax_id_type"
            options={taxIDTypeOptions.map(({ label, id_type }) => ({
              label,
              value: id_type,
            }))}
            placeholder="Select Tax ID type"
          />
          <Controller
            name={"tax_id"}
            control={control}
            rules={{ required: isTaxIDRequired, pattern: taxIDRegex }}
            render={({ field: { onChange, ...field } }) => (
              <div className="flex flex-col gap-2">
                <label className="block font-medium text-gray-900 text-md leading-6 dark:text-gray-100">
                  Tax ID
                </label>
                <Input
                  {...field}
                  onKeyDown={onTaxIdKeyDown(taxIDType)}
                  onChange={(event) => {
                    const taxID = event.target.value;

                    const isTaxIDCheckRequired =
                      isTaxIDRequired || !isAllAsterisks(taxID);

                    const isTaxIDInvalid = isTaxIDCheckRequired
                      ? !taxIDRegex.test(taxID)
                      : false;

                    if (isTaxIDInvalid) {
                      setError("tax_id", { message: "Invalid Tax ID" });
                    } else {
                      clearErrors("tax_id");
                    }
                    onChange(event);
                  }}
                  onBlur={(event) => {
                    const taxID = event.currentTarget.value;

                    const isTaxIDCheckRequired =
                      isTaxIDRequired || !isAllAsterisks(taxID);

                    if (
                      isTaxIDCheckRequired &&
                      !taxIDRegex.test(event.currentTarget.value)
                    ) {
                      setError("tax_id", { message: "Invalid Tax ID" });
                    }
                  }}
                  className="bg-white"
                  placeholder="Tax ID number"
                />
                {errors.tax_id?.message && (
                  <Text className="truncate text-rose-500 text-xs">
                    {errors.tax_id?.message}
                  </Text>
                )}
              </div>
            )}
          />
        </div>

        <h2 className="pt-2 font-medium text-lg">Residential Address</h2>
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
          <Form.TextInput
            type="text"
            label="Residential Address Line 1"
            name="street_address"
            placeholder="Street Address"
            autoComplete="address-line1"
            className="bg-white"
          />
          <Form.TextInput
            type="text"
            label="Address Line 2"
            name="unit"
            placeholder="Suite, Unit, Building, Floor, etc."
            autoComplete="address-line2"
            className="bg-white"
          />
        </div>

        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
          <Form.TextInput
            type="text"
            label="City"
            name="city"
            placeholder="City"
            autoComplete="address-level2"
            className="bg-white"
          />
          {isUSATaxResident ? (
            <div className="flex flex-col gap-2">
              <label className="block font-medium text-gray-900 text-md leading-6 dark:text-gray-100">
                State
              </label>
              <StateInput<UltimateBeneficialOwner | AuthorizedIndividual>
                name="state"
                control={form.control}
              />
              {errors.state?.message && (
                <Text className="truncate text-rose-500 text-xs">
                  {errors.state?.message}
                </Text>
              )}
            </div>
          ) : (
            <Form.TextInput
              type="text"
              label="State"
              name="state"
              placeholder="State"
              autoComplete="address-level1"
              className="bg-white"
            />
          )}
        </div>

        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
          <Form.TextInput
            type="text"
            label="Postal Code"
            name="postal_code"
            placeholder="Postal Code"
            autoComplete="postal-code"
            className="bg-white"
          />
        </div>

        <Text className="pt-8 pb-4 font-semibold text-lg">Affiliations</Text>
        <div className="flex flex-col gap-3">
          <YesNoCheckbox
            label="Are you an officer or 10% or greater shareholder of a publicly traded company, subject to the US Securities Exchange Act 1934?"
            setValue={(isChecked) => setValue("is_control_person", isChecked)}
            isChecked={isControlPerson}
          />
          {isControlPerson && (
            <Form.TextInput
              type="text"
              label="If yes, please provide the name(s) of the controlling firm(s), separated by commas."
              name="controlling_firms"
              className="bg-white"
            />
          )}
        </div>

        <div className="flex flex-col gap-3">
          <YesNoCheckbox
            label="Are you affiliated with or employed by a stock exchange, regulatory body, member firm of an exchange, FINRA or a municipal securities broker-dealer?"
            setValue={(isChecked) =>
              setValue("is_affiliated_exchange_or_finra", isChecked)
            }
            isChecked={isAffiliatedExchangeOrFinra}
          />
          {isAffiliatedExchangeOrFinra && (
            <Form.TextInput
              type="text"
              label="If yes, please provide the name of the affiliated firm."
              name="affiliated_firm"
              className="bg-white"
            />
          )}
        </div>

        <div className="flex flex-col gap-3">
          <YesNoCheckbox
            label="Are you currently or formerly a Politically Exposed Person or Public Official?"
            setValue={(isChecked) =>
              setValue("is_politically_exposed", isChecked)
            }
            isChecked={isPoliticallyExposed}
          />
        </div>

        <div className="flex flex-col gap-3">
          <YesNoCheckbox
            label="Do any of the above apply to an immediate family member?"
            setValue={(isChecked) =>
              setValue("immediate_family_exposed", isChecked)
            }
            isChecked={isImmediateFamilyExposed}
          />
        </div>
      </Form>

      <h2 className="mt-6 mb-3 font-medium text-lg">Documents</h2>
      <UploadID {...uploadID} />

      {type === "ubo" && (
        <div className="mt-6 flex flex-col gap-6">
          <OwnerDocumentField
            label="Proof of Address"
            name="address_verification"
            description="Upload a document verifying the owner's residential address, such as a utility bill or bank statement dated within the last 90 days."
            document={addressDoc}
          />
          <OwnerDocumentField
            label="Tax ID Document"
            name="tax_id_verification"
            description="Upload a document verifying the owner's tax identification number."
            document={taxIdDoc}
          />
        </div>
      )}

      <div
        className={classNames(
          "flex w-full items-center gap-4 pt-9",
          onDelete ? "justify-between" : "justify-end",
        )}
      >
        {onDelete && (
          <Button
            type="button"
            variant="red"
            disabled={isSubmitDisabled}
            onClick={onDelete}
          >
            Remove
          </Button>
        )}
        <Button
          type="submit"
          onClick={form.handleSubmit(onSubmit, onSubmitError)}
          disabled={isSubmitDisabled}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};
